import { useEffect, useRef, useState } from 'react';

import { useNavParams, useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';

import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

import { TabPanel } from 'components/shared';
import StyledTabs from 'components/shared/StyledTabs';

import { RecoilRoot } from 'recoil';
import EmbeddedDashboard from './embedded-dashboard/EmbeddedDashboard';
import QualityMonitor from './quality-monitor/QualityMonitor';
import { TravelExpenseClaims } from './travel-expense-claims';
import AbsenceNotices from 'components/shared/AbsenceNotices/AbsenceNotices';
import AutoAskDutyRoundsOverview from './auto-ask-duty-rounds-overview/AutoAskDutyRoundsOverview';
import useSubNavigation from 'hooks/useSubNavigation';
import TravelMovements from 'components/employee-card/employment/travel-movements/TravelMovements';

export enum TabType {
  QUALITY_MONITOR = 'quality-monitor',
  MESSAGES = 'messages',
  DASHBOARD = 'dashboard',
  TRAVEL_EXPENSE_CLAIMS = 'travel-expense-claims',
  TRAVEL_MOVEMENTS = 'travel-movements',
  ABSENCE_NOTICES = 'absence-notices',
  AI_MATCH = 'ai-match'
}

const RosterHome = () => {
  const { t } = useTranslation('common');
  const {
    hasRosterPortalMessagesAccess,
    hasEmbeddedDashboard,
    hasFeatureToggleExpenseClaims,
    hasAbsenceNoticesViewAccess,
    hasAutoAskDutyRoundsViewAccess,
    hasTravelMovementsFeature
  } = useSelector((state) => state.grants);
  const [isLoading, setIsLoading] = useState(false);
  const { params, removeParam } = useNavParams();

  const { activePage, setPage } = useSubNavigation<TabType>(
    TabType.QUALITY_MONITOR
  );

  const promoNodeRef = useRef(document.createElement('div'));

  const messagesTab = useRef<HTMLDivElement | null>(null);

  const onTabChanged = (_, newValue: TabType) => {
    setPage(newValue);
  };

  const onMessagesTabRender = () => {
    const container: HTMLDivElement = document.createElement('div');
    messagesTab.current?.appendChild(container);
    window.dojoTopic.publish('renderRosterMessages', container);
  };

  useEffect(() => {
    setIsLoading(false);

    return () => {
      if (activePage === TabType.MESSAGES) {
        window.dojoTopic.publish('destroyRosterMessages');
      }
    };
  }, [activePage]);

  // remove date param when switching from travel expense claims tab
  useEffect(() => {
    if (params?.date && activePage !== TabType.TRAVEL_EXPENSE_CLAIMS) {
      removeParam('date');
    }
  }, [params]);

  return (
    <StyledTabs>
      <Paper square>
        <div className="d-flex between v-center">
          <Tabs
            value={activePage}
            indicatorColor="primary"
            textColor="primary"
            onChange={onTabChanged}
            className="tabs"
          >
            <Tab label={t('qualityMonitor')} value={TabType.QUALITY_MONITOR} />
            {hasRosterPortalMessagesAccess && (
              <Tab label={t('messages')} value={TabType.MESSAGES} />
            )}
            {hasEmbeddedDashboard && (
              <Tab
                label={
                  <div className="promote-tab">
                    <span>Dashboard</span>
                  </div>
                }
                value={TabType.DASHBOARD}
              />
            )}
            {hasFeatureToggleExpenseClaims && (
              <Tab
                label={
                  <div className="dti-tab">
                    <span>{t('travelExpenseClaims.label')}</span>
                  </div>
                }
                value={TabType.TRAVEL_EXPENSE_CLAIMS}
              />
            )}
            {hasTravelMovementsFeature && (
              <Tab
                label={
                  <div className="dti-tab">
                    <span>{t('travelMovements.label')}</span>
                  </div>
                }
                value={TabType.TRAVEL_MOVEMENTS}
              />
            )}
            {hasAbsenceNoticesViewAccess && (
              <Tab
                label={
                  <div>
                    <span>{t('absenceNotices')}</span>
                  </div>
                }
                value={TabType.ABSENCE_NOTICES}
              />
            )}
            {hasAutoAskDutyRoundsViewAccess && (
              <Tab
                label={
                  <div>
                    <span>{t('aiPlanning')}</span>
                  </div>
                }
                value={TabType.AI_MATCH}
              />
            )}
          </Tabs>
          <div ref={promoNodeRef} />
        </div>
      </Paper>
      <div className="progress-container">
        {isLoading && <LinearProgress />}
      </div>
      <div className="content">
        <TabPanel
          value={TabType.QUALITY_MONITOR}
          activePage={activePage}
          title={t('qualityMonitor')}
          style={{ minHeight: '100%', height: 'auto' }}
        >
          <QualityMonitor setPage={setPage} setIsLoading={setIsLoading} />
        </TabPanel>
        {hasRosterPortalMessagesAccess && (
          <TabPanel
            value={TabType.MESSAGES}
            activePage={activePage}
            onRender={onMessagesTabRender}
            title={t('messages')}
            style={{ height: '100%', overflow: 'hidden' }}
          >
            <div
              className="dojo-content"
              ref={messagesTab}
              style={{ height: '100%' }}
            />
          </TabPanel>
        )}
        {hasEmbeddedDashboard && (
          <TabPanel
            title="Dashboard"
            value={TabType.DASHBOARD}
            activePage={activePage}
            style={{ height: 720, width: 1280, margin: 'auto' }}
          >
            <EmbeddedDashboard tabsPromoNode={promoNodeRef.current} />
          </TabPanel>
        )}
        {hasFeatureToggleExpenseClaims && (
          <TabPanel
            title={t('travelExpenseClaims.label')}
            value={TabType.TRAVEL_EXPENSE_CLAIMS}
            activePage={activePage}
          >
            <TravelExpenseClaims />
          </TabPanel>
        )}
        {hasTravelMovementsFeature && (
          <TabPanel
            title={t('travelMovements.label')}
            value={TabType.TRAVEL_MOVEMENTS}
            activePage={activePage}
          >
            <TravelMovements
              showActions
              isTravelMovementsBasedOnPlanUnit
              showEmployeeName
            />
          </TabPanel>
        )}
        {hasAbsenceNoticesViewAccess && (
          <TabPanel
            title={t('absenceNotices')}
            value={TabType.ABSENCE_NOTICES}
            activePage={activePage}
          >
            <RecoilRoot>
              <AbsenceNotices />
            </RecoilRoot>
          </TabPanel>
        )}
        {hasAutoAskDutyRoundsViewAccess && (
          <TabPanel
            title={t('aiPlanning')}
            value={TabType.AI_MATCH}
            activePage={activePage}
          >
            <RecoilRoot>
              <AutoAskDutyRoundsOverview />
            </RecoilRoot>
          </TabPanel>
        )}
      </div>
    </StyledTabs>
  );
};

export default RosterHome;
